import React from 'react';
import { Link } from 'react-router-dom';
import './WhatWeDo.css';

const WhatWeDo = () => {
  return (
    <div className="what-we-do">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-image-container">
          <picture>
            <source srcSet="/images/what-we-do-hero.avif" type="image/avif" />
            <source srcSet="/images/what-we-do-hero.webp" type="image/webp" />
            <img 
              src="/images/what-we-do-hero.jpg" 
              alt="Carbon capture and storage operations" 
              className="hero-image"
            />
          </picture>
          <div className="hero-image-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1>Our Integrated Business Model</h1>
            <p>
              We deliver comprehensive carbon capture and storage solutions through 
              an integrated approach that spans the entire project lifecycle.
            </p>
          </div>
        </div>
      </section>

      {/* Integration Diagram Section */}
      <section className="section integration-diagram-section">
        <div className="container">
          <div className="integration-diagram">
            <div className="diagram-elements">
              <div className="diagram-element development" data-stage="development">
                <div className="element-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>Development</span>
                <div className="element-detail">Site assessment & design</div>
              </div>
              
              <div className="diagram-element financing" data-stage="financing">
                <div className="element-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L13.5 2.5L3.5 12.5L2 14L8 20L9.5 18.5L19.5 8.5L21 7V9H21Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>Financing</span>
                <div className="element-detail">Structuring & funding</div>
              </div>
              
              <div className="diagram-element construction" data-stage="construction">
                <div className="element-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12,3L2,12H5V20H19V12H22L12,3M12,8.75A1.25,1.25 0 0,1 13.25,10A1.25,1.25 0 0,1 12,11.25A1.25,1.25 0 0,1 10.75,10A1.25,1.25 0 0,1 12,8.75Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>Construction</span>
                <div className="element-detail">EPC & commissioning</div>
              </div>
              
              <div className="diagram-element operations" data-stage="operations">
                <div className="element-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>Operations</span>
                <div className="element-detail">24/7 monitoring</div>
              </div>
              
              <div className="diagram-element ownership" data-stage="ownership">
                <div className="element-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,3.18L19,6.3V11C19,15.5 16.18,19.65 12,20.81C7.82,19.65 5,15.5 5,11V6.3L12,3.18Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>Ownership</span>
                <div className="element-detail">Long-term stewardship</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Model Overview */}
      <section className="section overview-section">
        <div className="container">
          <h2 className="section-title">End-to-End CCS Solutions</h2>
          <p className="section-subtitle">
            Our integrated business model ensures seamless project delivery from conception to long-term operation
          </p>
          
          <div className="business-model-grid">
            <div className="model-phase">
              <div className="phase-number">01</div>
              <h3>Development</h3>
              <p>
                Site assessment, feasibility studies, technology selection, and regulatory approvals. 
                We identify optimal locations and design custom solutions for each industrial facility.
              </p>
              <ul>
                <li>Geological surveys and site characterization</li>
                <li>Environmental impact assessments</li>
                <li>Technology optimization and design</li>
                <li>Regulatory compliance and permitting</li>
              </ul>
            </div>

            <div className="model-phase">
              <div className="phase-number">02</div>
              <h3>Structuring & Financing</h3>
              <p>
                Comprehensive financial structuring including project financing, carbon credit monetization, 
                and risk management to ensure project viability and optimal returns.
              </p>
              <ul>
                <li>Project finance structuring</li>
                <li>Carbon credit development and sales</li>
                <li>Risk assessment and mitigation</li>
                <li>Investment partnerships</li>
              </ul>
            </div>

            <div className="model-phase">
              <div className="phase-number">03</div>
              <h3>Construction</h3>
              <p>
                Full-scale construction management utilizing proven oil & gas industry practices 
                and cutting-edge CCS technology implementation.
              </p>
              <ul>
                <li>EPC (Engineering, Procurement, Construction)</li>
                <li>Quality assurance and safety management</li>
                <li>Technology integration and testing</li>
                <li>Commissioning and startup</li>
              </ul>
            </div>

            <div className="model-phase">
              <div className="phase-number">04</div>
              <h3>Operations</h3>
              <p>
                Long-term operations and maintenance ensuring optimal performance, 
                regulatory compliance, and continuous monitoring of storage integrity.
              </p>
              <ul>
                <li>24/7 monitoring and control systems</li>
                <li>Preventive maintenance programs</li>
                <li>Performance optimization</li>
                <li>Regulatory reporting and compliance</li>
              </ul>
            </div>

            <div className="model-phase">
              <div className="phase-number">05</div>
              <h3>Ownership</h3>
              <p>
                Long-term asset ownership and value creation through operational excellence, 
                carbon credit generation, and strategic partnerships.
              </p>
              <ul>
                <li>Asset management and optimization</li>
                <li>Carbon credit portfolio management</li>
                <li>Strategic partnerships and expansion</li>
                <li>Long-term storage stewardship</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Value Creation */}
      <section className="section value-creation-section">
        <div className="container">
          <div className="value-creation-content">
            <div className="value-text">
              <h2>Creating Value Through Integration</h2>
              <p>
                Our integrated approach creates multiple value streams while reducing risk 
                and ensuring optimal project outcomes for all stakeholders.
              </p>
              
              <div className="value-benefits">
                <div className="benefit">
                  <div>
                    <h4>Revenue Diversification</h4>
                    <p>Multiple revenue streams from carbon credits, storage services, and operational efficiency gains.</p>
                  </div>
                </div>
                
                <div className="benefit">
                  <div>
                    <h4>Operational Excellence</h4>
                    <p>Leveraging oil & gas industry expertise for reliable, efficient operations.</p>
                  </div>
                </div>
                
                <div className="benefit">
                  <div>
                    <h4>Risk Mitigation</h4>
                    <p>Comprehensive risk management across technical, financial, and regulatory dimensions.</p>
                  </div>
                </div>
                
                <div className="benefit">
                  <div>
                    <h4>Scalable Solutions</h4>
                    <p>Proven model that scales efficiently across different industries and geographies.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Expertise */}
      <section className="section technology-section">
        <div className="container">
          <h2 className="section-title">Technology & Expertise</h2>
          <p className="section-subtitle">
            Combining cutting-edge carbon capture technology with proven oil & gas industry expertise
          </p>
          
          <div className="technology-grid">
            <div className="tech-card">
              <h3>Advanced Capture Technology</h3>
              <p>
                State-of-the-art CO₂ separation and purification systems with industry-leading 
                efficiency rates and minimal energy consumption.
              </p>
            </div>
            
            <div className="tech-card">
              <h3>Oil & Gas Expertise</h3>
              <p>
                Decades of experience in subsurface operations, well drilling, and 
                geological storage from the oil and gas industry.
              </p>
            </div>
            
            <div className="tech-card">
              <h3>Digital Monitoring</h3>
              <p>
                Advanced monitoring and control systems ensuring safe, efficient operations 
                and real-time performance optimization.
              </p>
            </div>
            
            <div className="tech-card">
              <h3>Geological Storage</h3>
              <p>
                Comprehensive understanding of subsurface geology and proven methods 
                for permanent CO₂ storage in deep formations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Partner With Us</h2>
            <p>
              Ready to implement a comprehensive carbon capture solution for your facility? 
              Our integrated approach ensures successful project delivery and long-term value creation.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Start Your Project
              </Link>
              <Link to="/" className="btn btn-secondary">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo; 